// mantine
import { Badge, Menu, Table, UnstyledButton } from '@mantine/core';
// components
// icons
import { EllipsisVertical, ImagePlay, Trash2 } from 'lucide-react';
// utils
import type { TRegistryItem } from '@newo/api/gateway';
import type { TVersionControlRegistryVersion } from '@/services/bff/bff.queries';
import DateTime from '@/modules/DateTime/components/DateTime';
import { DATE_FORMAT } from '@/const';

interface VersionItemProps {
    item: TVersionControlRegistryVersion;
    isProjectsV2Enabled?: boolean;
    isPublishPending: boolean;
    onDelete: (item: TRegistryItem) => void;
    onPublish: (item: TVersionControlRegistryVersion) => void;
}

const isLegacyRegistryVersion = (
    item: TVersionControlRegistryVersion
): item is TRegistryItem => 'active_project_count' in item;

const VersionItem = ({
    item,
    isProjectsV2Enabled = false,
    isPublishPending,
    onDelete,
    onPublish
}: VersionItemProps) => {
    const handlePublish = () => {
        onPublish(item);
    };

    const handleDelete = () => {
        if (isLegacyRegistryVersion(item)) {
            onDelete(item);
        }
    };
    const activeProjectCount = isLegacyRegistryVersion(item)
        ? item.active_project_count
        : undefined;

    return (
        <Table.Tr>
            <Table.Td className="truncate">{item.idn}</Table.Td>
            <Table.Td>{item.version}</Table.Td>
            {isProjectsV2Enabled ? undefined : (
                <Table.Td>{activeProjectCount}</Table.Td>
            )}
            <Table.Td>
                <Badge
                    color={item.is_public ? 'green' : 'gray'}
                    size="xs"
                    variant={item.is_public ? 'filled' : 'light'}
                >
                    {item.is_public ? 'Deployed' : 'Not deployed'}
                </Badge>
            </Table.Td>
            <Table.Td>
                <DateTime
                    format={DATE_FORMAT.SHORT_12H}
                    value={item.created_at}
                />
            </Table.Td>
            <Table.Td>
                {item.published_at ? (
                    <DateTime
                        format={DATE_FORMAT.SHORT_12H}
                        value={item.published_at}
                    />
                ) : (
                    <span className="text-gray-4">&mdash;</span>
                )}
            </Table.Td>
            <Table.Td>
                {!isProjectsV2Enabled || !item.is_public ? (
                    <Menu withArrow arrowPosition="side">
                        <Menu.Target>
                            <UnstyledButton
                                aria-label={`Actions for ${item.idn} ${item.version}`}
                                className="relative left-1"
                                data-testid={`registry-module-actions-${item.id}`}
                            >
                                <EllipsisVertical />
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {item.is_public ? undefined : (
                                <Menu.Item
                                    data-testid={`registry-module-publish-${item.id}`}
                                    disabled={isPublishPending}
                                    leftSection={<ImagePlay />}
                                    onClick={handlePublish}
                                >
                                    {isLegacyRegistryVersion(item)
                                        ? 'Deploy'
                                        : 'Publish'}
                                </Menu.Item>
                            )}
                            {isLegacyRegistryVersion(item) ? (
                                <Menu.Item
                                    className="text-red-6"
                                    leftSection={<Trash2 />}
                                    onClick={handleDelete}
                                >
                                    Delete
                                </Menu.Item>
                            ) : undefined}
                        </Menu.Dropdown>
                    </Menu>
                ) : undefined}
            </Table.Td>
        </Table.Tr>
    );
};

export default VersionItem;
